import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { User, Group, CreateExpenseData } from '../types';
import { apiService } from '../services/apiService';
import { useToast } from '../hooks/useToast';
import { useKeyboardScroll, useAutoFocus } from '../hooks/useKeyboardScroll';
import { haptics } from '../utils/haptics';
import { logger } from '../utils/logger';
import styles from './AddExpenseChat.module.css';

interface AddExpenseChatProps {
  user: User;
  groups: Group[];
  initialGroupId?: string; // From context (personal or specific group)
  onExpenseCreated: (groupId: string) => void;
  onCancel: () => void;
}

interface ParsedExpense {
  title: string;
  amount: number;
  category: string;
  description?: string;
}

const CATEGORIES = [
  { icon: '🍔', name: 'Food', value: 'Food' },
  { icon: '🚗', name: 'Transport', value: 'Transport' },
  { icon: '🎬', name: 'Fun', value: 'Entertainment' },
  { icon: '🛍️', name: 'Shopping', value: 'Shopping' },
  { icon: '💡', name: 'Utilities', value: 'Utilities' },
  { icon: '✈️', name: 'Travel', value: 'Travel' },
  { icon: '🏥', name: 'Health', value: 'Healthcare' },
  { icon: '📚', name: 'Education', value: 'Education' },
];

const getCurrencySymbol = (currency: string): string => {
  const symbols: { [key: string]: string } = {
    USD: '$', EUR: '€', GBP: '£', RUB: '₽', KZT: '₸'
  };
  return symbols[currency] || currency;
};

const AddExpenseChat: React.FC<AddExpenseChatProps> = ({
  user,
  groups,
  initialGroupId,
  onExpenseCreated,
  onCancel,
}) => {
  const { addToast } = useToast();
  
  const inputRef = useRef<HTMLInputElement>(null);
  useAutoFocus(inputRef, true);
  useKeyboardScroll(inputRef);
  
  // Find personal group
  const personalGroup = useMemo(() => 
    groups.find(g => g.name === 'Personal' || g.name === 'My Expenses'),
    [groups]
  );
  
  // Smart group detection from context - memoized for stable reference
  const { selectedGroup, isPersonalExpense } = useMemo(() => {
    let group: Group | undefined;
    let isPersonal = false;
    
    if (initialGroupId) {
      // Specific group ID provided - find it
      group = groups.find(g => g.id === initialGroupId);
      
      // Check if it's a personal group
      if (group && (group.name === 'Personal' || group.name === 'My Expenses')) {
        isPersonal = true;
      }
    } else {
      // No context - default to first group (or personal if exists)
      group = personalGroup || groups[0];
      isPersonal = !!personalGroup;
    }
    
    // Fallback to first group if nothing found
    if (!group && groups.length > 0) {
      group = groups[0];
    }
    
    // Double-check personal detection if we have a selected group
    if (group && !isPersonal) {
      isPersonal = group.name === 'Personal' || group.name === 'My Expenses';
    }
    
    return { selectedGroup: group, isPersonalExpense: isPersonal };
  }, [groups, initialGroupId, personalGroup]);
  
  const [inputText, setInputText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedExpense, setParsedExpense] = useState<ParsedExpense | null>(null);
  
  // Initialize expense state with selected group
  const [expense, setExpense] = useState<CreateExpenseData>(() => ({
    title: '',
    description: '',
    amount: 0,
    currency: selectedGroup?.currency || user.preferredCurrency || 'USD',
    category: 'Food',
    groupId: selectedGroup?.id || '',
    shares: []
  }));
  
  // Update expense when selectedGroup changes
  useEffect(() => {
    if (selectedGroup) {
      setExpense(prev => ({
        ...prev,
        groupId: selectedGroup.id,
        currency: selectedGroup.currency || prev.currency || user.preferredCurrency || 'USD'
      }));
    }
  }, [selectedGroup, user.preferredCurrency]);
  
  const [editingField, setEditingField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Auto-select all members for group expenses (equal split)
  useEffect(() => {
    if (selectedGroup && !isPersonalExpense && selectedGroup.members) {
      const memberIds = new Set(selectedGroup.members.map(m => m.user.id));
      const equalAmount = expense.amount > 0 && memberIds.size > 0 
        ? expense.amount / memberIds.size 
        : 0;
      
      const shares = Array.from(memberIds).map(userId => ({
        userId,
        amount: parseFloat(equalAmount.toFixed(2))
      }));
      
      setExpense(prev => ({
        ...prev,
        groupId: selectedGroup.id,
        currency: selectedGroup.currency || prev.currency,
        shares
      }));
    }
  }, [selectedGroup, expense.amount, isPersonalExpense]);
  
  const handleParse = useCallback(async () => {
    if (!inputText.trim()) return;
    
    setParsing(true);
    try {
      const result = await apiService.parseExpenseText(inputText);
      
      if (result?.parsed) {
        const parsed = result.parsed;
        const newExpense: ParsedExpense = {
          title: parsed.title || inputText,
          amount: parsed.amount || 0,
          category: parsed.category || 'Food',
          description: parsed.description || ''
        };
        
        setParsedExpense(newExpense);
        
        // Update expense state
        const updatedExpense: CreateExpenseData = {
          ...expense,
          title: newExpense.title,
          amount: newExpense.amount,
          category: newExpense.category,
          description: newExpense.description || '',
          groupId: selectedGroup?.id || expense.groupId,
        };
        
        setExpense(updatedExpense);
        
        // For group expenses, recalculate shares
        if (selectedGroup && !isPersonalExpense && selectedGroup.members) {
          const memberIds = new Set(selectedGroup.members.map(m => m.user.id));
          const equalAmount = newExpense.amount / memberIds.size;
          const shares = Array.from(memberIds).map(userId => ({
            userId,
            amount: parseFloat(equalAmount.toFixed(2))
          }));
          
          setExpense(prev => ({ ...prev, shares }));
        }
        
        addToast({
          type: 'success',
          message: '✨ Expense parsed! Tap any field to edit',
          duration: 2000
        });
        
        // Clear input
        setInputText('');
      }
    } catch (error: unknown) {
      logger.error('Parse error:', error);
      addToast({
        type: 'error',
        message: 'Failed to parse expense. Please try again.',
        duration: 3000
      });
    } finally {
      setParsing(false);
    }
  }, [inputText, expense, selectedGroup, isPersonalExpense, addToast]);
  
  const handleFieldEdit = (field: string, value: any) => {
    setExpense(prev => ({ ...prev, [field]: value }));
    setEditingField(null);
    
    // If amount changed, recalculate shares for groups
    if (field === 'amount' && selectedGroup && !isPersonalExpense) {
      const memberIds = new Set(selectedGroup.members.map(m => m.user.id));
      const equalAmount = value / memberIds.size;
      const shares = Array.from(memberIds).map(userId => ({
        userId,
        amount: parseFloat(equalAmount.toFixed(2))
      }));
      setExpense(prev => ({ ...prev, shares }));
    }
  };
  
  const handleConfirm = useCallback(async () => {
    if (!expense.groupId || expense.amount <= 0 || !expense.title) {
      addToast({
        type: 'error',
        message: 'Please fill in all required fields',
        duration: 3000
      });
      return;
    }
    
    // For group expenses, validate shares
    if (!isPersonalExpense) {
      if (expense.shares.length === 0) {
        addToast({
          type: 'error',
          message: 'Please select at least one person to split with',
          duration: 3000
        });
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const expenseData = {
        ...expense,
        paidById: user.id
      };
      
      await apiService.createExpense(expenseData);
      
      haptics.expenseAdded();
      addToast({
        type: 'success',
        message: '✅ Expense added!',
        duration: 2000
      });
      
      onExpenseCreated(expense.groupId);
    } catch (error: unknown) {
      logger.error('Failed to create expense:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to add expense';
      addToast({
        type: 'error',
        message: errorMessage,
        duration: 3000
      });
    } finally {
      setSubmitting(false);
    }
  }, [expense, user.id, isPersonalExpense, addToast, onExpenseCreated]);
  
  const selectedCategory = CATEGORIES.find(c => c.value === expense.category);
  
  // Safety check - don't render if no group available
  if (!selectedGroup) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>⚠️</div>
          <div className={styles.emptyText}>No groups available</div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={styles.chatContainer}>
      {/* Header */}
      <div className={styles.chatHeader}>
        <button className={styles.backButton} onClick={onCancel}>
          ←
        </button>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>Add Expense</div>
          <div className={styles.headerSubtitle}>
            {isPersonalExpense ? '💼 Personal Expenses' : `🏠 ${selectedGroup?.name || 'Group'}`}
          </div>
        </div>
      </div>
      
      {/* Chat Area */}
      <div className={styles.chatArea}>
        {!parsedExpense ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <div className={styles.emptyText}>
              {isPersonalExpense 
                ? 'Type what you spent on...'
                : 'Type what you spent on for this group...'}
            </div>
            <div className={styles.emptyExamples}>
              <div className={styles.example}>Coffee $5</div>
              <div className={styles.example}>Dinner 60</div>
              <div className={styles.example}>Taxi 25</div>
            </div>
          </div>
        ) : (
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              <div className={styles.previewTitle}>Preview</div>
              <div className={styles.previewHint}>Tap any field to edit</div>
            </div>
            
            {/* Title - Editable */}
            <div 
              className={`${styles.previewField} ${editingField === 'title' ? styles.editing : ''}`}
              onClick={() => !editingField && setEditingField('title')}
            >
              <div className={styles.fieldHeader}>
                <div className={styles.fieldLabel}>What</div>
                {!editingField && <div className={styles.editHint}>Tap to edit</div>}
              </div>
              {editingField === 'title' ? (
                <input
                  type="text"
                  className={styles.fieldInput}
                  value={expense.title}
                  onChange={(e) => handleFieldEdit('title', e.target.value)}
                  onBlur={() => setEditingField(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setEditingField(null);
                    }
                  }}
                  autoFocus
                  placeholder="Expense title"
                />
              ) : (
                <div className={styles.fieldValue}>
                  {expense.title || 'Tap to add'}
                  <span className={styles.editIcon}>✏️</span>
                </div>
              )}
            </div>
            
            {/* Amount - Editable */}
            <div 
              className={`${styles.previewField} ${editingField === 'amount' ? styles.editing : ''}`}
              onClick={() => !editingField && setEditingField('amount')}
            >
              <div className={styles.fieldHeader}>
                <div className={styles.fieldLabel}>Amount</div>
                {!editingField && <div className={styles.editHint}>Tap to edit</div>}
              </div>
              {editingField === 'amount' ? (
                <div className={styles.amountEditWrapper}>
                  <select
                    className={styles.currencySelect}
                    value={expense.currency}
                    onChange={(e) => {
                      setExpense(prev => ({ ...prev, currency: e.target.value }));
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="USD">USD $</option>
                    <option value="EUR">EUR €</option>
                    <option value="GBP">GBP £</option>
                    <option value="RUB">RUB ₽</option>
                    <option value="KZT">KZT ₸</option>
                  </select>
                  <input
                    type="number"
                    className={styles.fieldInput}
                    value={expense.amount || ''}
                    onChange={(e) => handleFieldEdit('amount', parseFloat(e.target.value) || 0)}
                    onBlur={() => setEditingField(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setEditingField(null);
                      }
                    }}
                    autoFocus
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </div>
              ) : (
                <div className={styles.fieldValue}>
                  {getCurrencySymbol(expense.currency)}{expense.amount > 0 ? expense.amount.toFixed(2) : '0.00'}
                  <span className={styles.editIcon}>✏️</span>
                </div>
              )}
            </div>
            
            {/* Category - Editable */}
            <div 
              className={`${styles.previewField} ${editingField === 'category' ? styles.editing : ''}`}
              onClick={() => !editingField && setEditingField('category')}
            >
              <div className={styles.fieldHeader}>
                <div className={styles.fieldLabel}>Category</div>
                {!editingField && <div className={styles.editHint}>Tap to edit</div>}
              </div>
              {editingField === 'category' ? (
                <div className={styles.categoryPicker}>
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      className={`${styles.categoryOption} ${expense.category === cat.value ? styles.selected : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFieldEdit('category', cat.value);
                      }}
                    >
                      {cat.icon} {cat.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className={styles.fieldValue}>
                  {selectedCategory?.icon} {selectedCategory?.name || expense.category}
                  <span className={styles.editIcon}>✏️</span>
                </div>
              )}
            </div>
            
            {/* Split Info (only for groups) */}
            {!isPersonalExpense && selectedGroup && (
              <div className={styles.splitInfo}>
                <div className={styles.splitLabel}>Split equally</div>
                <div className={styles.splitDetails}>
                  {selectedGroup.members.length} people • {getCurrencySymbol(expense.currency)}
                  {(expense.amount / selectedGroup.members.length).toFixed(2)} each
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className={styles.previewActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => {
                  setParsedExpense(null);
                  setExpense({
                    title: '',
                    description: '',
                    amount: 0,
                    currency: selectedGroup?.currency || user.preferredCurrency || 'USD',
                    category: 'Food',
                    groupId: selectedGroup?.id || '',
                    shares: []
                  });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={handleConfirm}
                disabled={submitting || expense.amount <= 0}
              >
                {submitting ? 'Adding...' : '✓ Confirm'}
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Input Area */}
      <div className={styles.inputArea}>
        <div className={styles.inputWrapper}>
          <input
            ref={inputRef}
            type="text"
            className={styles.textInput}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !parsing) {
                e.preventDefault();
                handleParse();
              }
            }}
            placeholder={isPersonalExpense ? "What did you spend on?" : "What did you spend on?"}
            disabled={parsing || !!parsedExpense}
          />
          <button
            type="button"
            className={`${styles.parseButton} ${parsing ? styles.parsing : ''}`}
            onClick={handleParse}
            disabled={!inputText.trim() || parsing || !!parsedExpense}
          >
            {parsing ? '⏳' : '✨'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddExpenseChat;

